// TODO Try using setDefaultCamera when toggling between views
import React, {
  Suspense,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import PropTypes from "prop-types";
import { OrbitControls } from "@react-three/drei";
import { timeYear } from "d3";
import { a } from "@react-spring/three";
import * as THREE from "three";
import { useSpring, config } from "@react-spring/core";
import { useFrame, useResource, useThree } from "react-three-fiber";
// import { useStore } from "../../store";

// import { SelectiveBloom } from "./SelectiveBloom";
import {
  // formatTimeYear,
  CHART_DIMENSIONS,
  LAYOUT,
  accessors,
  BLANK_AXIS_NAME,
  ATTRIBUTE_TYPE,
  setFromKeys,
} from "../../utils";

import { OrthographicXAxis, XAxis } from "./XAxis";
import Spheres from "./Spheres";
import Lines from "./Lines";
import { FocusOnMesh, meshPositions } from "./MeshUtils";
import { AxisText } from "./Axis/Text";
import { DreiLine } from "./Axis/Line";
import { AssociationPlane } from "./Axis/Planes";

// TODO Make time offset generic (provide custom function)
export const Timeline = ({
  associations,
  attributes,
  axisTransitionConfig = {
    associations: { delay: (d) => 0 },
    grid: { delay: (d) => 0 },
    targets: { delay: (d) => 0 },
  },
  bloomIntensity = 0.5,
  filteredAttributes,
  centerMeshAroundOrigin = true,
  enablePan = true,
  enableRotate = true,
  enableZoom = true,
  material = {},
  onTimelineFocus = () => {},
  onAssociationClick = () => {},
  orthographic = false,
  resetOrbitControls = false,
  scales,
  scaleTimelineWidth = 1,
  showAssociationPlanes = true,
  showGridLinesTargets = true,
  showGridLinesFloorX = false,
  showGridLinesFloorZ = false,
  showDates = true,
  showGlobal = false,
  showTargets = true,
  showTargetNames = false,
  // refers to all transitions that use useSpring
  springTransitionConfig = { ...config.default, duration: 1000 },
  rotateCarpetAssociationText = [0, 0, 0],
  rotation = [0, 0, 0],
  textAssociation = {
    anchorX: "right",
    x: -1,
  },
  topView = false,
  translateX = 0,
  translateY = -5,
  // translateYOrthographic = -8.5,
  translateYCarpetView = 0,
  translateZ = 0,
  translateMeshOnly = [0, 0, 0],
  transitionConfig = {},
  visible = true,
  zoomToData = true,
  zoomOnMesh = 1,
}) => {
  const cameraControlsRef = useRef();
  const timelineRef = useResource();
  const textRef = useResource();
  const xAxisRef = useResource();
  const scene = useRef();
  // const sceneElements = useRef();
  const { gl, camera } = useThree();

  const colorFunc = useCallback((d) => accessors.color(d), []);
  const opacityFunc = useCallback((d) => accessors.opacity(d), []);
  const zeroFunc = useCallback((d) => 0, []);

  // const staticNameAxisPosition = useMemo(() => new THREE.Vector3(0, 0, 10), []);

  const tmpEuler = useMemo(() => new THREE.Euler(), []);
  const tmpQuaternion = useMemo(() => new THREE.Quaternion(), []);
  // const tmpVector = useMemo(() => new THREE.Vector3(), []);

  const {
    x0: meshX0,
    width: meshWidth,
    position: meshPosition,
  } = meshPositions({
    data: filteredAttributes,
    centerMeshAroundOrigin,
    topView,
    translate: [
      // these were using separate props previously,
      // translateXOrthographic and
      // translateYOrthographic
      orthographic ? translateX : translateX,
      topView ? CHART_DIMENSIONS.Y.MAX / 2 + translateYCarpetView : translateY,
      // orthographic
      //   ? translateY
      //   : topView
      //   ? CHART_DIMENSIONS.Y.MAX / 2 + translateYCarpetView
      //   : translateY,
      translateZ,
    ],
  });

  const activeTargetNames = useMemo(() => {
    return setFromKeys(
      filteredAttributes.filter(
        (d) => accessors.type(d) === ATTRIBUTE_TYPE.TARGETED
      ),
      accessors.association,
      accessors.target.name
    );
  }, [filteredAttributes]);

  useEffect(() => {
    if (!orthographic) {
      textRef.current.position.x = meshPosition[0];
    }
  }, [orthographic, meshPosition, textRef]);

  // TODO this is responsible for throwing an error when the
  // component dismounts
  useFrame(() => {
    gl.autoClear = false;
    gl.clearDepth();
    // cameraControlsRef.current.update();
    if (orthographic) {
      // console.log(cameraControlsRef.current.object);
      gl.autoClear = true;
      cameraControlsRef.current.enabled = false;
      camera.position.y = 0;
      cameraControlsRef.current.object.position.y = 0;
      cameraControlsRef.current.target.y = 0;
      cameraControlsRef.current.update();
      textRef.current.position.x = cameraControlsRef.current.target.x - 4;
      // textRef.current.position.copy(cameraControlsRef.current.target);
      cameraControlsRef.current.enabled = true;
      // timelineRef.current.scale.x =
      //   useStore.getState().app.timeline.scales.width;
      // textRef.current.position.y = 0;
      // xAxisRef.current.position.y = 0;
      // timelineRef.current.position.y = 0;
      // cameraControlsRef.current.object.porsition.y = 0;
      //   cameraControlsRef.current.update();
      //   textRef.current.position.sub(camera.position, staticNameAxisPosition);
    }
    // visible && gl.render(sceneElements.current, camera);
    visible && gl.render(scene.current, camera);
    // console.log(cameraControlsRef.current.object.position);
  }, 1);

  const meshOnlyPosition = [
    meshPosition[0] + translateMeshOnly[0],
    meshPosition[1] + translateMeshOnly[1],
    meshPosition[2] + translateMeshOnly[2],
  ];

  // event callbacks for interactions with shapes ===================
  const focusedShapeData = useRef(null);
  const onShapeFocus = useCallback((data) => {
    // intercept the data from either Lines/Spheres
    focusedShapeData.current = data;
  }, []);

  // we need a shared function above Lines & Spheres because
  // clicking anywhere on their canvas would invoke their respective
  // onPointerDown / onPointerMissed callbacks
  const onTimelineClick = useCallback(
    (e) => {
      e.stopPropagation();
      if (focusedShapeData.current) {
        onTimelineFocus(focusedShapeData.current);
      } else {
        onTimelineFocus([]);
      }
    },
    [focusedShapeData, onTimelineFocus]
  );
  // END event callbacks for interactions with shapes ===============

  // TODO Generate Z-Axis and Y-Axis elements based on filteredAttributes

  // Reset camera when orthographic mode is enabled
  // or we request camera be reset
  useEffect(() => {
    cameraControlsRef.current.reset();
  }, [resetOrbitControls, orthographic]);

  // useEffect(() => {
  //   if (!cameraControlsRef.current) return;
  //   cameraControlsRef.current.addEventListener("change", (e) => {
  //     console.log(e);
  //   });
  // }, [cameraControlsRef]);

  const {
    // refers to axes and such
    meshPosition: aMeshPosition,
    // used for "cheating" in our narrative and only moving the data
    meshOnlyPosition: aMeshOnlyPosition,
    scaleTimelineWidth: aScaleTimelineWidth,
    // meshRotation: aMeshRotation,
    // meshQRotation: aMeshQRotation,
  } = useSpring({
    from: { delta: 0 },
    to: {
      delta: 1,
      meshPosition,
      // meshOnlyPosition: [
      //   orthographic
      //     ? meshOnlyPosition[0] - (scaleTimelineWidth * meshWidth) / 2
      //     : meshOnlyPosition[0],
      //   meshOnlyPosition[1],
      //   meshOnlyPosition[2],
      // ],
      meshOnlyPosition,
      meshRotation: topView ? [Math.PI / 2, 0, 0] : rotation,
      meshQRotation: tmpQuaternion.setFromEuler(
        tmpEuler.set(topView ? Math.PI / 2 : 0, 0, 0)
      ),
      scaleTimelineWidth,
    },

    config: springTransitionConfig,
    onStart: () => (cameraControlsRef.current.enabled = false),
    onChange: ({ delta, meshRotation = [] }) => {
      tmpEuler.set(...meshRotation);
      tmpQuaternion.setFromEuler(tmpEuler);
      textRef.current.quaternion.slerp(tmpQuaternion, delta);
      xAxisRef.current.quaternion.slerp(tmpQuaternion, delta);
      timelineRef.current.quaternion.slerp(tmpQuaternion, delta);
    },
    onRest: () => (cameraControlsRef.current.enabled = true),
  });

  return (
    true && (
      <>
        <OrbitControls
          ref={cameraControlsRef}
          onChange={(e) => {
            console.log("changed", e);
          }}
          enablePan={enablePan}
          enableZoom={enableZoom}
          enableRotate={enableRotate}
          onPointerDown={() => {}}
          minAzimuthAngle={-1.1}
          maxAzimuthAngle={0.66}
          // minPolarAngle={0}
          minPolarAngle={Math.PI / 4}
          maxPolarAngle={Math.PI / 2}
          maxDistance={150}
        />
        <scene visible={visible} ref={scene}>
          <a.group
            ref={textRef}
            // applyQuaternion={aMeshQRotation}
            // quaternion={aMeshQRotation}
            // rotation={aMeshRotation}
            position={aMeshPosition}
          >
            {Object.entries(associations).map(
              ([associationName, s], associationIdx) => {
                const endX = scales.x.range()[1];
                return (
                  <a.group key={`association-3d-group-${associationName}`}>
                    <a.group position-z={s.z(associationName)}>
                      <Suspense>
                        {/* This text is for ASSOCIATIONS ie. countries */}
                        <AxisText
                          x={-1}
                          y={
                            CHART_DIMENSIONS.Y.MIN +
                            LAYOUT.TIMELINE.EVENTS.CONTEXTUAL.OFFSET
                          }
                          z={0}
                          onPointerDown={onAssociationClick}
                          delay={axisTransitionConfig.associations.delay(
                            -1,
                            CHART_DIMENSIONS.Y.MIN +
                              LAYOUT.TIMELINE.EVENTS.CONTEXTUAL.OFFSET,
                            s.z(associationName)
                          )}
                          color={`rgb(255, 255, 255)`}
                          visible={
                            !orthographic &&
                            showTargets &&
                            !showTargetNames &&
                            !topView
                          }
                          font={"assets/fonts/GT-Zirkon-Regular.woff"}
                          anchorX={"right"}
                          // rotation={[0, 0, 0]}
                          rotation={rotateCarpetAssociationText}
                          depthTest={true}
                          fontSize={0.5}
                          capitalize={true}
                          text={associationName}
                          {...textAssociation}
                        />
                        {s.y.domain().map((target, targetIdx) => {
                          // does this target exist in the attributes?
                          const showY = LAYOUT.TIMELINE.AXES
                            .HIDE_INACTIVE_GRID_ELEMENTS
                            ? activeTargetNames.has(
                                `${associationName}-${target}`
                              )
                            : true;

                          let gridLineOpacity = 0;
                          if (showY && showGridLinesTargets && !topView) {
                            gridLineOpacity = targetIdx % 2 === 1 ? 0 : 0.1;
                          }
                          return (
                            !target.startsWith(BLANK_AXIS_NAME) && (
                              <group
                                key={`axis-y-${associationName}-${target}`}
                              >
                                {/* This text is for TARGET NAMES */}
                                <AxisText
                                  capitalize={false}
                                  x={-1}
                                  y={s.y(target)}
                                  z={0}
                                  delay={axisTransitionConfig.targets.delay(
                                    -1,
                                    s.y(target),
                                    0
                                  )}
                                  color={`rgb(255, 255, 255)`}
                                  visible={
                                    showY &&
                                    showTargets &&
                                    showTargetNames &&
                                    !topView
                                  }
                                  font={"assets/fonts/GT-Zirkon-Regular.woff"}
                                  anchorX={"right"}
                                  rotation={[0, 0, 0]}
                                  depthTest={true}
                                  fontSize={0.5}
                                  text={target}
                                  // text={showTargetNames ? target : `•`}
                                />

                                {/* {gridLineOpacity && ( */}
                                <DreiLine
                                  points={[
                                    [0, s.y(target), 0],
                                    [endX * scaleTimelineWidth, s.y(target), 0],
                                  ]}
                                  linewidth={1}
                                  width={1}
                                  opacity={gridLineOpacity}
                                  color={0xffffff}
                                />
                                {/* )} */}
                              </group>
                            )
                          );
                        })}
                        <AxisText
                          x={-1}
                          y={CHART_DIMENSIONS.Y.MIN}
                          z={0}
                          color={`rgb(255, 255, 255)`}
                          visible={showTargets && topView}
                          font={"assets/fonts/GT-Zirkon-Regular.woff"}
                          anchorX={"right"}
                          rotation={[-Math.PI / 2, 0, 0]}
                          depthTest={true}
                          fontSize={
                            LAYOUT.TIMELINE.AXIS.VERTICAL.FONT_SIZE * 0.75
                          }
                          text={associationName.toUpperCase()}
                        />
                        <DreiLine
                          points={[
                            [
                              0,
                              CHART_DIMENSIONS.Y.MIN +
                                LAYOUT.TIMELINE.EVENTS.CONTEXTUAL.OFFSET,
                              0,
                            ],
                            [
                              endX,
                              CHART_DIMENSIONS.Y.MIN +
                                LAYOUT.TIMELINE.EVENTS.CONTEXTUAL.OFFSET,
                              0,
                            ],
                          ]}
                          linewidth={1}
                          width={1}
                          opacity={
                            !topView && showGridLinesFloorZ
                              ? associationIdx % 2 === 0
                                ? 0.2
                                : 0.1
                              : 0
                          }
                          color={0xffffff}
                        />
                        delay=
                        {axisTransitionConfig.grid.delay(
                          0,
                          CHART_DIMENSIONS.Y.MIN +
                            LAYOUT.TIMELINE.EVENTS.CONTEXTUAL.OFFSET,
                          s.z(associationName)
                        )}
                        {!topView && showAssociationPlanes && (
                          <AssociationPlane
                            x={scales.x}
                            y={s.y}
                            z={0}
                            opacity={0.05}
                          />
                        )}
                      </Suspense>
                    </a.group>
                  </a.group>
                );
              }
            )}
          </a.group>
          <a.group ref={xAxisRef} position={aMeshPosition}>
            {orthographic ? (
              <OrthographicXAxis
                maxFontSize={
                  topView ? LAYOUT.TIMELINE.AXIS.VERTICAL.FONT_SIZE * 0.75 : 0.5
                }
                textRotation={[0, 0, Math.PI / 2]}
                textVisible={showDates}
                xFunc={scales.time}
                zoomLevel={scaleTimelineWidth}
                yPosition={
                  CHART_DIMENSIONS.Y.MIN +
                  2 * LAYOUT.TIMELINE.EVENTS.CONTEXTUAL.OFFSET
                }
              />
            ) : (
              <XAxis
                fontSize={
                  topView ? LAYOUT.TIMELINE.AXIS.VERTICAL.FONT_SIZE * 0.75 : 0.5
                }
                opacity={!topView && showGridLinesFloorX ? 0.2 : 0}
                textRotation={[
                  orthographic ? 0 : topView ? -Math.PI / 2 : -Math.PI / 4,
                  0,
                  0,
                ]}
                textVisible={showDates}
                transitionConfig={axisTransitionConfig}
                xFunc={scales.time}
                years={scales.time.ticks(timeYear.every(1))}
              />
            )}
          </a.group>
          {/* </scene> */}
          {/* <scene visible={visible} ref={sceneElements}> */}
          <a.group
            ref={timelineRef}
            scale-x={aScaleTimelineWidth}
            // quaternion={aMeshQRotation}
            // rotation={aMeshRotation}
            position={aMeshOnlyPosition}
            onPointerDown={onTimelineClick}
            onPointerMissed={onTimelineClick}
          >
            <Lines
              data={attributes.filter((a) => a.type === `global`)}
              scaleX={(d) => d.scaleX}
              x={(d) => d.x}
              y={(d) => d.y}
              // z={orthographic ? zeroFunc : (d) => d.z}
              z={(d) => d.z}
              material={material}
              selected={filteredAttributes.filter((a) => a.type === "global")}
              height={scales.z.step()}
              width={LAYOUT.TIMELINE.LINES.GLOBAL.WIDTH}
              depth={LAYOUT.TIMELINE.LINES.GLOBAL.DEPTH}
              colorFunc={colorFunc}
              onHover={topView ? onShapeFocus : () => {}}
              opacityFunc={
                topView ? opacityFunc : showGlobal ? opacityFunc : zeroFunc
              }
              renderFuzziness={true}
              rotation={[Math.PI / 2, 0, 0]}
              show={true}
              showTooltip={topView}
              transitionConfig={transitionConfig}
              name={`contextual-events`}
            />
            <Spheres
              data={attributes.filter((a) => a.type === `targeted`)}
              scaleX={(d) => d.scaleX}
              x={(d) => d.x}
              y={(d) => (topView ? CHART_DIMENSIONS.Y.MIN * 2 : d.y)}
              // z={orthographic ? zeroFunc : (d) => d.z}
              z={(d) => d.z}
              material={material}
              selected={
                filteredAttributes.filter((a) => a.type === "targeted")
                // .map((d) => accessors.id(d))
              }
              colorFunc={colorFunc}
              onHover={onShapeFocus}
              opacityFunc={opacityFunc}
              // TODO This logic should not be here
              makeHole={true}
              makeHoleFunc={(d) =>
                Number(accessors.event.type(d) === "Zero Click")
              }
              showTooltip={true}
              // opacity={0.7}
              size={
                topView
                  ? LAYOUT.TIMELINE.SPHERES.SIZE
                  : CHART_DIMENSIONS.Y.BANDWIDTH / 4
              }
              show={true}
              transitionConfig={transitionConfig}
            />
            <Lines
              data={attributes.filter((a) => a.type === `contextual`)}
              scaleX={(d) => d.scaleX}
              x={(d) => d.x}
              y={(d) => d.y}
              // z={orthographic ? zeroFunc : (d) => d.z}
              z={(d) => d.z}
              material={material}
              // selected={[26, 27]}
              selected={filteredAttributes.filter(
                (a) => a.type === "contextual"
              )}
              height={Object.values(associations)[0].y.step()} // TODO This is hacky
              width={LAYOUT.TIMELINE.LINES.CONTEXTUAL.WIDTH}
              colorFunc={colorFunc}
              onHover={topView ? () => {} : onShapeFocus}
              opacityFunc={topView ? zeroFunc : opacityFunc}
              renderFuzziness={true}
              show={true}
              showTooltip={!topView}
              transitionConfig={transitionConfig}
            />
          </a.group>
          {/* <ambientLight color={0x0000ff} intensity={1} /> */}
        </scene>
        {zoomToData && (
          <FocusOnMesh
            mesh={timelineRef}
            springTransitionConfig={springTransitionConfig}
            width={meshWidth}
            meshStart={meshX0}
            zoomOnMesh={zoomOnMesh}
          />
        )}
        {/* <GlobalEffects bloomIntensity={bloomIntensity} /> */}
      </>
    )
  );
};

Timeline.propTypes = {
  associations: PropTypes.object.isRequired,
  attributes: PropTypes.array.isRequired,
  filteredAttributes: PropTypes.array.isRequired,
  scales: PropTypes.object.isRequired,
};
